import type { SettingsChipTone } from '@affine/component/setting-components';
import { I18n } from '@affine/i18n';

import type { SigningProfileState } from './signing-readiness';

const STATE_KEYS = {
  ready: 'com.streamlain.pdf.signature.profile.state.ready',
  needs_activation:
    'com.streamlain.pdf.signature.profile.state.needsActivation',
  activation_failed:
    'com.streamlain.pdf.signature.profile.state.activationFailed',
  disabled: 'com.streamlain.pdf.signature.profile.state.disabled',
  connection_unavailable:
    'com.streamlain.pdf.signature.profile.state.connectionUnavailable',
  connection_not_allowed:
    'com.streamlain.pdf.signature.profile.state.connectionNotAllowed',
  certificate_unusable:
    'com.streamlain.pdf.signature.profile.state.certificateUnusable',
  certificate_pin_missing:
    'com.streamlain.pdf.signature.profile.state.certificatePinMissing',
} as const satisfies Record<SigningProfileState, string>;

const HINT_KEYS = {
  needs_activation: 'com.streamlain.pdf.signature.profile.hint.needsActivation',
  activation_failed:
    'com.streamlain.pdf.signature.profile.hint.activationFailed',
  disabled: 'com.streamlain.pdf.signature.profile.hint.disabled',
  connection_unavailable:
    'com.streamlain.pdf.signature.profile.hint.connectionUnavailable',
  connection_not_allowed:
    'com.streamlain.pdf.signature.profile.hint.connectionNotAllowed',
  certificate_unusable:
    'com.streamlain.pdf.signature.profile.hint.certificateUnusable',
  certificate_pin_missing:
    'com.streamlain.pdf.signature.profile.hint.certificatePinMissing',
} as const satisfies Record<Exclude<SigningProfileState, 'ready'>, string>;

/** Ready reads as fine, a pending activation as a to-do, a fault as a fault. */
export const SIGNING_PROFILE_STATE_TONES = {
  ready: 'green',
  needs_activation: 'orange',
  activation_failed: 'red',
  disabled: 'grey',
  connection_unavailable: 'red',
  connection_not_allowed: 'red',
  certificate_unusable: 'red',
  certificate_pin_missing: 'red',
} as const satisfies Record<SigningProfileState, SettingsChipTone>;

/** How a signing profile stands, in one or two words. */
export function getSigningProfileStateLabel(state: SigningProfileState) {
  return I18n.t(STATE_KEYS[state]);
}

/** What keeps a profile from signing and how that is settled. */
export function getSigningProfileStateHint(
  state: Exclude<SigningProfileState, 'ready'>
) {
  return I18n.t(HINT_KEYS[state]);
}
